import React, { useState, useEffect } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { ThemeProvider } from './contexts/ThemeContext';
import { Navbar } from './components/Navbar';
import { DashboardPage } from './pages/DashboardPage';
import { AuthGate } from './components/AuthGate';
import { kpiThemes } from './data/kpiCatalog';
import { missingAuthVars, AUTH_DOMAIN, AUTH_CLIENT_ID, AUTH_AUDIENCE } from './lib/auth';

// Business areas come from the KPI catalog's themes, so the nav and the per-area
// views always agree. Revenue is the default (it's the area with live data).
const AREAS = kpiThemes.map((t) => t.theme);

function areaFromUrl(): string {
  const a = new URLSearchParams(window.location.search).get('area') ?? '';
  return AREAS.includes(a) ? a : 'Revenue';
}

const Shell: React.FC = () => {
  const [area, setArea] = useState<string>(areaFromUrl);

  const handleArea = (next: string) => {
    setArea(next);
    const params = new URLSearchParams(window.location.search);
    params.set('area', next);
    window.history.pushState(null, '', `?${params.toString()}`);
  };

  useEffect(() => {
    const onPop = () => setArea(areaFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return (
    <ThemeProvider>
      <div id='top' className='min-h-screen bg-surface-secondary'>
        <Navbar areas={AREAS} area={area} onArea={handleArea} />
        <DashboardPage area={area} />
      </div>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  // Auth0 config is required in every environment — fail loudly rather than
  // silently falling back to an unauthenticated dashboard.
  if (missingAuthVars.length > 0) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-surface-secondary px-4 text-center text-sm text-on-surface-secondary'>
        Missing required Auth0 config: {missingAuthVars.join(', ')}
      </div>
    );
  }
  return (
    <Auth0Provider
      domain={AUTH_DOMAIN!}
      clientId={AUTH_CLIENT_ID!}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH_AUDIENCE,
      }}
      cacheLocation='localstorage'
      useRefreshTokens
      useRefreshTokensFallback
    >
      <AuthGate>
        <Shell />
      </AuthGate>
    </Auth0Provider>
  );
};

export default App;
